import React from 'react'
import { PlusOutlined } from '@ant-design/icons'
import { Collapse } from 'antd'
import cn from 'classnames'
import { useTranslation } from 'lib/i18n'
import { FAQPageJsonLd } from 'next-seo'

import { BodyText, H1, H6 } from 'components/UI/Typography/Typography.new'

import styles from './Faq.module.css'

const { Panel } = Collapse

type FaqProps = {
  faqList: {
    question: string
    answer: string
  }[]
}

type ExpandIcon = {
  isActive: boolean
}

const ExpandIcon = ({ isActive }: ExpandIcon): JSX.Element => (
  <div data-cy="faqButton">
    <PlusOutlined
      className={cn(styles.icon, {
        [styles.active]: isActive,
      })}
      rotate={isActive ? 45 : 0}
    />
  </div>
)

const Faq = ({ faqList }: FaqProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div className={styles.wrapper} data-cy="faq">
      <div className={styles.title}>
        <H1 element="h2">
          {t('BusinessPageFaq.title', 'Frequently asked questions')}
        </H1>
      </div>
      <Collapse
        className={styles.collapse}
        defaultActiveKey={['1']}
        expandIconPosition="right"
        bordered={false}
        expandIcon={ExpandIcon}
      >
        {faqList.map((faq) => (
          <Panel
            header={
              <div className={styles.header} data-cy="faqQuestion">
                <H6 element="h3">{faq.question}</H6>
              </div>
            }
            key={faq.question}
            className={styles.panel}
          >
            <div data-cy="faqAnswer">
              <BodyText>{faq.answer}</BodyText>
            </div>
          </Panel>
        ))}
      </Collapse>

      <FAQPageJsonLd
        mainEntity={faqList.map((faq) => ({
          questionName: faq.question,
          acceptedAnswerText: faq.answer,
        }))}
      />
    </div>
  )
}

export default Faq
